import { faClock, faTriangleExclamation, faUsers } from '@fortawesome/pro-regular-svg-icons';
import type { TFunction } from 'i18next';

import type { IIntegrationUIStat } from '@feathr/components';
import { moment } from '@feathr/hooks';

/** Shown for a value that failed to load. */
export const UNAVAILABLE_VALUE = '—';

/** The summary bar values. A value that failed to load is `null` and named in `errors`. */
export interface ICrmSyncSummary {
  last_synced: string | null;
  mappings_need_attention: number | null;
  /** The values that failed to load. A summary without the list has none. */
  errors?: readonly string[];
  /** The synced record count, e.g. `constituents_synced`. */
  [key: string]: unknown;
}

/** `null` means the request failed, so every value is unavailable. */
export type TCrmSyncSummaryResult = ICrmSyncSummary | null;

export interface ICrmSyncSummaryRecords<TRecordsKey extends string> {
  /** The summary field with the synced record count, e.g. `contacts_synced`. */
  key: TRecordsKey;
  /** The stat label, e.g. "Contacts synced". */
  label: string;
}

function isUnavailable(summary: TCrmSyncSummaryResult, field: string): boolean {
  return !summary || !!summary.errors?.includes(field);
}

function formatCount(summary: TCrmSyncSummaryResult, field: string): string {
  const count = summary?.[field];
  return isUnavailable(summary, field) || typeof count !== 'number'
    ? UNAVAILABLE_VALUE
    : count.toLocaleString();
}

/** How long ago the summary last synced, or why it can't say. */
export function formatLastSynced(summary: TCrmSyncSummaryResult, t: TFunction): string {
  if (!summary || isUnavailable(summary, 'last_synced')) {
    return UNAVAILABLE_VALUE;
  }
  // The header shows the date, so show how fresh the data is here.
  return summary.last_synced ? moment.utc(summary.last_synced).fromNow() : t('Never');
}

/** The summary bar's stats, in display order. */
export function getCrmSyncSummaryStats<TRecordsKey extends string>(
  summary: TCrmSyncSummaryResult,
  records: ICrmSyncSummaryRecords<TRecordsKey>,
  t: TFunction,
): IIntegrationUIStat[] {
  const needAttention = summary?.mappings_need_attention ?? 0;
  return [
    {
      key: 'last_synced',
      label: t('Last checked'),
      value: formatLastSynced(summary, t),
      icon: faClock,
    },
    {
      key: records.key,
      label: records.label,
      value: formatCount(summary, records.key),
      icon: faUsers,
    },
    {
      key: 'mappings_need_attention',
      label: t('Mappings need attention'),
      value: formatCount(summary, 'mappings_need_attention'),
      icon: faTriangleExclamation,
      attention: needAttention > 0,
    },
  ];
}

/** The header's last sync time: the summary's once it has one, else the integration's own. */
export function getHeaderLastSyncedAt(
  summary: TCrmSyncSummaryResult | undefined,
  fallback: string | undefined,
): string | null {
  if (summary && !summary.errors?.includes('last_synced')) {
    return summary.last_synced;
  }
  return fallback ?? null;
}
